import React, { useEffect, useState } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { MenuIcon, X as CloseIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
export const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [openSubmenuIndex, setOpenSubmenuIndex] = useState(-1); // No submenu open by default
  const {
    scrollY
  } = useScroll();
  // Update navbar appearance based on scroll position
  useEffect(() => {
    const updateScrollState = () => {
      setHasScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', updateScrollState);
    updateScrollState(); // Initial check
    return () => window.removeEventListener('scroll', updateScrollState);
  }, []);
  const toggleSubmenu = index => {
    setOpenSubmenuIndex(openSubmenuIndex === index ? -1 : index);
  };
  const menuItems = [{
    label: 'Products',
    submenu: [{
      label: 'Pigeon Documents',
      url: 'https://www.pigeondocuments.com'
    }, {
      label: 'Pigeon Immigration',
      url: 'https://www.pigeonimmigration.com'
    }, {
      label: 'Pigeon Logistics',
      url: 'https://www.pigeononboarding.com'
    }, {
      label: 'Pigeon Subjectivities',
      url: 'https://www.pigeonsubjectivities.com'
    }]
  }, {
    label: 'How It Works',
    url: '#how-it-works'
  }, {
    label: 'Integrations',
    url: '#integrations'
  }, {
    label: 'Security',
    url: '#security'
  }];
  return <nav className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 ${hasScrolled ? 'backdrop-blur-md bg-white/90 shadow-[0_4px_30px_rgba(46,107,255,0.08)] border-b border-[#E7EAF0]/40' : 'backdrop-blur-sm bg-white/70 border-b border-[#E7EAF0]/20'}`} data-id="element-413">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-414">
        <div className="flex justify-between items-center h-16" data-id="element-415">
          {/* Logo and Navigation */}
          <div className="flex items-center" data-id="element-416">
            <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.5
          }} className="flex-shrink-0" data-id="element-417">
              <a href="/" className="flex items-center" data-id="element-418">
                <img src="https://i.imgur.com/W2X0iTc.png" alt="Pigeon Logo" className="h-10 mr-2" data-id="element-419" />
                <span className="text-xl font-bold flex flex-col -space-y-2" style={{
                fontFamily: 'Helvetica, sans-serif'
              }} data-id="element-420">
                  <span style={{
                  color: '#1432f4'
                }} data-id="element-421">
                    Pigeon
                  </span>
                  <span style={{
                  color: '#000000'
                }} data-id="element-422">
                    Documents
                  </span>
                </span>
              </a>
            </motion.div>
            <div className="hidden md:block ml-6" data-id="element-423">
              <div className="flex items-center space-x-4" data-id="element-424">
                {menuItems.map((item, index) => <div key={index} className="relative group" data-id="element-425">
                    <motion.button className="text-[#5A6376] hover:text-[#0B1220] px-2 py-1.5 rounded-md text-sm font-medium flex items-center whitespace-nowrap" whileHover={{
                  y: -1
                }} transition={{
                  type: 'spring',
                  stiffness: 300
                }} data-id="element-426">
                      {item.url ? <a href={item.url} data-id="element-427">{item.label}</a> : item.label}
                      {item.submenu && <ChevronDownIcon className="ml-1 h-4 w-4 transition-transform duration-200 group-hover:rotate-180" data-id="element-428" />}
                    </motion.button>
                    {item.submenu && <div className="absolute left-0 mt-2 w-56 rounded-xl shadow-lg bg-white/90 backdrop-blur-md border border-[#E7EAF0]/60 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 overflow-hidden" data-id="element-429">
                        <div className="py-1" data-id="element-430">
                          {item.submenu.map((subitem, subindex) => <motion.a key={subindex} href={subitem.url} className="block px-4 py-2.5 text-sm text-[#0B1220] hover:bg-[#F8F9FB]/80 border-l-2 border-transparent hover:border-[#2E6BFF]" whileHover={{
                      x: 2,
                      backgroundColor: '#F0F5FF'
                    }} transition={{
                      type: 'spring',
                      stiffness: 300
                    }} data-id="element-431">
                              {subitem.label}
                            </motion.a>)}
                        </div>
                      </div>}
                  </div>)}
              </div>
            </div>
          </div>
          {/* CTA Buttons */}
          <div className="hidden md:flex items-center space-x-3" data-id="element-432">
            <motion.a href="https://app.pigeondocuments.com/login" className="text-[#5A6376] hover:text-[#0B1220] px-2 py-2 rounded-md text-sm font-medium transition-colors duration-200 whitespace-nowrap" whileHover={{
            y: -1
          }} transition={{
            type: 'spring',
            stiffness: 300
          }} data-id="element-433">
              Log In
            </motion.a>
            <motion.a href="https://calendly.com/pigeondocuments" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-[#2E6BFF] to-[#36D1DC] hover:from-[#2059E2] hover:to-[#32C3CE] text-white px-6 py-3 rounded-full text-base font-medium transition-all duration-300 shadow-[0_4px_14px_rgba(46,107,255,0.25)] whitespace-nowrap" whileHover={{
            y: -1,
            scale: 1.02,
            boxShadow: '0 6px 20px rgba(46,107,255,0.35)'
          }} whileTap={{
            scale: 0.98
          }} data-id="element-434">
              Book a Call
            </motion.a>
          </div>
          {/* Mobile CTA and Menu Button */}
          <div className="md:hidden flex items-center space-x-2" data-id="element-435">
            <motion.a href="https://calendly.com/pigeondocuments" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-[#2E6BFF] to-[#36D1DC] hover:from-[#2059E2] hover:to-[#32C3CE] text-white px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-300 shadow-[0_4px_14px_rgba(46,107,255,0.25)] whitespace-nowrap" whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} data-id="element-436">
              Book a Call
            </motion.a>
            <motion.button onClick={() => setIsMenuOpen(!isMenuOpen)} className="inline-flex items-center justify-center p-2 rounded-md text-[#5A6376] hover:text-[#0B1220] focus:outline-none" whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} aria-expanded={isMenuOpen} aria-label="Toggle menu" data-id="element-437">
              {isMenuOpen ? <CloseIcon className="h-6 w-6" data-id="element-438" /> : <MenuIcon className="h-6 w-6" data-id="element-439" />}
            </motion.button>
          </div>
        </div>
      </div>
      {/* Mobile menu */}
      {isMenuOpen && <motion.div initial={{
      opacity: 0,
      height: 0
    }} animate={{
      opacity: 1,
      height: 'auto'
    }} exit={{
      opacity: 0,
      height: 0
    }} transition={{
      duration: 0.3,
      ease: 'easeInOut'
    }} className="md:hidden bg-white/95 backdrop-blur-md border-b border-[#E7EAF0]/40 shadow-lg" data-id="element-440">
          <div className="px-4 pt-2 pb-4 space-y-1" data-id="element-441">
            {menuItems.map((item, index) => <div key={index} className="py-1" data-id="element-442">
                {item.submenu ? <motion.button onClick={() => toggleSubmenu(index)} className="text-[#0B1220] block px-3 py-2.5 rounded-md text-base font-medium w-full text-left flex items-center justify-between" whileHover={{
            backgroundColor: '#F0F5FF'
          }} transition={{
            type: 'spring',
            stiffness: 300
          }} data-id="element-443">
                    <span data-id="element-444">{item.label}</span>
                    {openSubmenuIndex === index ? <ChevronUpIcon className="h-4 w-4" data-id="element-445" /> : <ChevronDownIcon className="h-4 w-4" data-id="element-446" />}
                  </motion.button> : <motion.a href={item.url} className="text-[#0B1220] block px-3 py-2.5 rounded-md text-base font-medium w-full text-left" whileHover={{
            y: -2,
            backgroundColor: '#F0F5FF'
          }} transition={{
            type: 'spring',
            stiffness: 300
          }} data-id="element-447">
                    {item.label}
                  </motion.a>}
                <AnimatePresence data-id="element-448">
                  {item.submenu && openSubmenuIndex === index && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} exit={{
              opacity: 0,
              height: 0
            }} transition={{
              duration: 0.3
            }} className="pl-4 space-y-1 mt-1 border-l-2 border-[#F0F5FF] ml-3" data-id="element-449">
                      {item.submenu.map((subitem, subindex) => <motion.a key={subindex} href={subitem.url} className="text-[#5A6376] block px-3 py-2 rounded-md text-sm" whileHover={{
                x: 2,
                backgroundColor: '#F0F5FF'
              }} transition={{
                type: 'spring',
                stiffness: 300
              }} data-id="element-450">
                          {subitem.label}
                        </motion.a>)}
                    </motion.div>}
                </AnimatePresence>
              </div>)}
            <div className="pt-2 mt-2 border-t border-[#E7EAF0]" data-id="element-451">
              <motion.a href="https://app.pigeondocuments.com/login" className="text-[#0B1220] block px-3 py-2.5 rounded-md text-base font-medium" whileHover={{
            x: 2,
            backgroundColor: '#F0F5FF'
          }} transition={{
            type: 'spring',
            stiffness: 300
          }} data-id="element-452">
                Log In
              </motion.a>
            </div>
          </div>
        </motion.div>}
    </nav>;
};